import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { RearLoadingFrictionlessGuard } from 'src/app/rear-loading/services/rear-loading-frictionless.guard';
import { PRESENCE } from 'src/app/shared/utils/constants';
import { selectSettings } from 'src/app/stores/settings';
import { GANDALF_HOST } from 'src/environments/runtime';

import { ISensor } from '../../interfaces/ISensor';
import { ApiErrorHandlerService } from '../../services/api-error-handler/api-error-handler.service';
import { FrodoSettings } from '../../services/appconfig/settings.model';
import { DeviceService } from '../../services/device/device.service';
import { HelmsdeepService } from '../../services/helmsdeep/helmsdeep.service';
import { KillSwitchService } from '../../services/kill-switch';
import { SocketService } from '../../services/socket/socket.service';
import { RearLoadingStore } from '../../services/store/rear-loading/rear-loading.store';
import { FRICTIONLESS_FLOW, PRESCRIPTIVE_FLOW, TOAST_TYPES } from '../../utils/constants';

@Component({
  selector: 'frodo-launch-screen',
  templateUrl: './launch-screen.component.html',
  styleUrls: ['./launch-screen.component.scss'],
})
export class LaunchScreenComponent implements OnInit, OnDestroy {
  private lockerIsFullMessage = 'common.lockerIsFullMessage';
  private validateLockerStatusErrorMessage = 'common.validateLockerStatusErrorMessage';
  configScreen: any;
  vision10kFlow = true;
  availableSpaces = false;
  positions = [];
  subscription: Subscription = new Subscription();
  enable: boolean;
  defaultScreen: string;
  launchScreenMessage: string;
  deviceSn: string;
  validatePresenceSensor = false;
  flow: string;
  enableOverflowArea: boolean;
  frodoSettings: FrodoSettings;
  launchScreenEnabled: boolean;

  constructor(
    private apiErrorHandlerService: ApiErrorHandlerService,
    private router: Router,
    private store: Store,
    private helmsdeepService: HelmsdeepService,
    private socketService: SocketService,
    private deviceService: DeviceService,
    private killSwitchService: KillSwitchService,
    private rearLoadingStore: RearLoadingStore,
    private rearLoadingFrictionlessGuard: RearLoadingFrictionlessGuard
  ) {
    this.socketService.setSocketHost(GANDALF_HOST);
    this.subscription.add(
      this.deviceService.getSerialNum.subscribe((serialNum) => {
        this.deviceSn = serialNum;
      })
    );
  }

  ngOnInit(): void {
    localStorage.removeItem('scheduleId');
    localStorage.removeItem('dispenseId');
    localStorage.removeItem('schedule');
    this.subscription.add(
      this.store.select(selectSettings).subscribe((settings) => {
        this.vision10kFlow = settings.defaultSettings.vision10kFlow;
        this.flow = this.vision10kFlow ? FRICTIONLESS_FLOW : PRESCRIPTIVE_FLOW;
        this.validatePresenceSensor = settings.device.validatePresenceSensor;
        this.enableOverflowArea = settings.device.enableOverflowArea;
        this.frodoSettings = settings.apps.frodo;
        this.enable = this.frodoSettings.homescreen.enable;
        this.defaultScreen = this.frodoSettings.homescreen.defaultScreen;
        this.configScreen = this.frodoSettings.style;

        if (this.vision10kFlow) {
          this.launchScreenMessage = this.frodoSettings.launchScreen.frictionlessMessage;
          if (this.validatePresenceSensor) this.checkForPresence();
        } else {
          this.validateAvailableSpaces();
          this.launchScreenMessage = this.frodoSettings.launchScreen.prescriptiveMessage;
          // TODO: launchScreenEnabled should be deprecated in favor of use RearLoadingLaunchScreenService to determine the resting screen based on the flow type
          this.launchScreenEnabled = this.frodoSettings.launchScreen.enable ?? true;
          if (!this.launchScreenEnabled) this.navigate();
        }
      })
    );
    this.killSwitchService.handleKillSwitchMode();
  }

  checkForPresence() {
    this.socketService.listen(PRESENCE, (data) => {
      const parsedData: ISensor = JSON.parse(data.msg.body);
      const sensorChanges = parsedData[this.deviceSn];
      const changes = Array.isArray(sensorChanges) ? sensorChanges[0] : sensorChanges;
      if (changes) {
        const presence = changes.eventType === 'FILL';
        const positions = this.positions.length
          ? this.positions
          : this.rearLoadingStore.getSessionPositions();
        const positionAlreadyTracked = positions.includes(changes.position);
        this.positions = this.rearLoadingFrictionlessGuard.updatePositionsForPresence(
          positions,
          changes.position,
          presence
        );
        this.rearLoadingStore.updateSessionPositions(this.positions);
        if (presence && !positionAlreadyTracked) {
          this.navigate();
        }
      } else {
        console.log('DeviceSn', this.deviceSn, 'does not matching', parsedData);
      }
    });
  }

  navigate(): void {
    if (this.enable) {
      switch (this.defaultScreen) {
        case 'order-id':
        case 'scan':
          this.router.navigate([
            'rear-loading/create-order',
            {
              type: this.defaultScreen,
              flow: this.flow,
              positions: JSON.stringify(this.positions),
            },
          ]);
          break;
        case 'mobile':
          this.router.navigate([
            'rear-loading/create-order-by-phone',
            {
              type: this.defaultScreen,
              flow: this.flow,
              positions: JSON.stringify(this.positions),
            },
          ]);
          break;
        case 'delivery-list':
          this.router.navigate([
            'rear-loading/delivery-list',
            {
              flow: this.flow,
            },
          ]);
          break;
      }
    } else {
      this.router.navigate(['rear-loading/home', { positions: JSON.stringify(this.positions) }]);
    }
  }

  validateAvailableSpaces(): void {
    this.subscription.add(
      this.helmsdeepService.getAvailableSpacesCount().subscribe(
        (response) => {
          if (response.count > 0) {
            this.availableSpaces = true;
          } else {
            this.availableSpaces = false;
            this.apiErrorHandlerService.showMessage(this.lockerIsFullMessage, TOAST_TYPES.Error);
          }
        },
        (error) => {
          this.availableSpaces = false;
          this.apiErrorHandlerService.handleApiError(error, this.validateLockerStatusErrorMessage);
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();

    if (this.socketService && this.socketService.socket) {
      this.socketService.socket.off(PRESENCE);
    }
  }
}
